// Deep linking utilities for navigating to specific tabs and sections within modules
// Supports query parameter-based navigation for granular control

/**
 * Generates a deep link to a specific tab and section in the College Strategy page
 * @param {string} collegeId - The college ID
 * @param {string} tab - The tab to open ('strategy', 'actions', 'application', 'research')
 * @param {string} section - Optional section within the tab to expand
 * @param {boolean} expand - Whether to expand the section (default: true)
 * @returns {string} - The deep link URL
 */
export const generateCollegeStrategyDeepLink = (collegeId, tab = 'strategy', section = null, expand = true) => {
  const baseUrl = `/college/strategy/${collegeId}`;
  const params = new URLSearchParams();

  if (tab) {
    params.append('tab', tab);
  }

  if (section) {
    params.append('section', section);
  }

  if (section && expand) {
    params.append('expand', 'true');
  }

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Task type to deep link mapping
 * Maps common timeline task patterns to their target tab/section in College Strategy
 */
export const TASK_DEEP_LINK_MAP = {
  // Value Proposition tasks
  'value proposition': { tab: 'strategy', section: 'valueProposition' },
  'unique value': { tab: 'strategy', section: 'valueProposition' },
  'why this school': { tab: 'strategy', section: 'valueProposition' },

  // Strategic Research tasks
  'strategic research': { tab: 'research', section: 'strategicResearch' },
  'school research': { tab: 'research', section: 'strategicResearch' },
  'research programs': { tab: 'research', section: 'strategicResearch' },
  'campus culture': { tab: 'research', section: 'strategicResearch' },

  // Application Requirements tasks
  'application requirement': { tab: 'application', section: null },
  'application component': { tab: 'application', section: null },
  'submit application': { tab: 'application', section: null },
  'complete application': { tab: 'application', section: null },

  // Action Items tasks
  'action item': { tab: 'actions', section: null },
  'immediate action': { tab: 'actions', section: 'immediateActions' },
  'next step': { tab: 'actions', section: null },

  // Essay tasks (if in context of a specific college)
  'supplemental essay': { tab: 'application', section: 'essayPrompts' },
  'essay prompt': { tab: 'application', section: 'essayPrompts' },

  // General strategy
  'fit analysis': { tab: 'strategy', section: null },
  'positioning': { tab: 'strategy', section: 'positioning' },
};

/**
 * Gets the deep link target for a given task based on its title/description
 * @param {Object} task - The task object with title/description
 * @param {string} collegeId - The college ID
 * @returns {Object|null} - Object with { tab, section } or null if no match
 */
export const getDeepLinkForTask = (task, collegeId) => {
  const taskText = (task.title || task.task_name || task.description || '').toLowerCase();

  // Check each pattern in the map
  for (const [pattern, target] of Object.entries(TASK_DEEP_LINK_MAP)) {
    if (taskText.includes(pattern)) {
      return {
        url: generateCollegeStrategyDeepLink(collegeId, target.tab, target.section),
        ...target
      };
    }
  }

  return null;
};

/**
 * Parses deep link parameters from URL search params
 * @param {URLSearchParams} searchParams - The URL search parameters
 * @returns {Object} - Parsed parameters { tab, section, expand }
 */
export const parseDeepLinkParams = (searchParams) => {
  return {
    tab: searchParams.get('tab') || null,
    section: searchParams.get('section') || null,
    expand: searchParams.get('expand') === 'true'
  };
};

/**
 * Section name to expandedSections key mapping
 * Maps URL section parameters to component state keys
 */
export const SECTION_KEY_MAP = {
  'valueProposition': 'valueProposition',
  'strategicResearch': 'strategicResearch',
  'immediateActions': 'immediateActions',
  'primaryDevelopment': 'primaryDevelopment',
  'positioning': 'positioning',
  'essayPrompts': 'essayPrompts',
  'completeBreakdown': 'completeBreakdown',
  'detailedStats': 'detailedStats'
};

/**
 * Gets the expandedSections state update for a given section parameter
 * @param {string} section - The section name from URL params
 * @returns {Object} - State update object for expandedSections
 */
export const getSectionExpansionState = (section) => {
  if (!section || !SECTION_KEY_MAP[section]) {
    return {};
  }

  return {
    [SECTION_KEY_MAP[section]]: true
  };
};
